import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useMeta } from '../useMeta';

const translations = {
  en: {
    title: "Privacy Policy",
    lastUpdated: "Last updated: February 2026",
    intro: "This Privacy Policy explains how ERC Unlock (\"we\", \"us\", or \"our\") collects, uses, and protects information when you use our website at ercunlock.com (the \"Service\"). By using our Service, you agree to the practices described in this policy.",
    sections: [
      {
        heading: "1. Information We Collect",
        subSections: [
          { heading: "Information you provide", text: "When you use our contact form, we collect your name, email address, and the content of your message. This information is used solely to respond to your enquiry and is not shared with third parties." },
          { heading: "ERC codes you enter", text: "The 16-digit ERC serial numbers you enter into our tool are processed in your browser to generate an unlock code. We do not store, log, or transmit these codes to our servers. All calculations happen client-side on your device." },
          { heading: "Payment information", text: "If you purchase credits, payment is processed by Stripe, a third-party payment processor. We do not store your card details. Stripe may collect and process your payment information in accordance with their own privacy policy, available at stripe.com/privacy." },
          { heading: "Usage data", text: "We use Google Analytics to collect anonymised information about how visitors use our site, including pages visited, time spent on the site, and general geographic location (country level). This data helps us improve the Service. You can opt out of Google Analytics by installing the Google Analytics Opt-out Browser Add-on." },
          { heading: "Cookies", text: "Our Service uses cookies for the following purposes:", list: ["Essential cookies: Used to store your free unlock count and purchased credits locally in your browser. These are required for the Service to function.", "Analytics cookies: Used by Google Analytics to help us understand how visitors interact with our site.", "Advertising cookies: We use Google AdSense to display advertisements. Google may use cookies to serve ads based on your prior visits to our website or other sites."] }
        ]
      },
      {
        heading: "2. How We Use Your Information",
        text: "We use the information we collect to:",
        list: ["Provide and maintain the ERC unlock tool", "Process payments for credit purchases", "Respond to enquiries submitted through our contact form", "Analyse usage patterns to improve the Service", "Display relevant advertisements through Google AdSense"]
      },
      {
        heading: "3. Third-Party Services",
        text: "Our Service integrates with the following third-party services:",
        list: ["Stripe — Payment processing. Privacy policy: stripe.com/privacy", "Google Analytics — Usage analytics. Privacy policy: policies.google.com/privacy", "Google AdSense — Advertising. Privacy policy: policies.google.com/privacy"],
        footer: "We are not responsible for the privacy practices of these third-party services."
      },
      {
        heading: "4. Data Retention",
        text: "Contact form submissions are retained for up to 12 months for the purpose of responding to enquiries. Credit and usage data stored in your browser (localStorage) remains on your device until you clear your browser data."
      },
      {
        heading: "5. Your Rights",
        text: "Depending on your location, you may have the right to:",
        list: ["Access the personal information we hold about you", "Request correction of inaccurate information", "Request deletion of your personal information", "Opt out of marketing communications"],
        footer: "To exercise any of these rights, please contact us using the contact form on this website."
      },
      {
        heading: "6. Children's Privacy",
        text: "Our Service is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will take steps to delete it."
      },
      {
        heading: "7. Changes to This Policy",
        text: "We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the \"Last updated\" date at the top of this page. Your continued use of the Service after any changes constitutes your acceptance of the updated policy."
      }
    ],
    contactHeading: "8. Contact",
    contactText: "If you have any questions about this Privacy Policy or how we handle your data, please use the",
    contactLink: "contact form",
    contactSuffix: "on our website."
  },
  ja: {
    title: "プライバシーポリシー",
    lastUpdated: "最終更新：2026年2月",
    intro: "このプライバシーポリシーは、ERC Unlock（「当社」）がercunlock.comのご利用にあたって情報をどのように収集、使用、保護するかを説明するものです。",
    sections: [
      { heading: "1. 収集する情報", subSections: [
        { heading: "お客様が提供する情報", text: "お問い合わせフォームをご利用の際、お名前、メールアドレス、メッセージ内容を収集します。この情報はお問い合わせへの返答のみに使用され、第三者と共有されません。" },
        { heading: "入力されたERCコード", text: "ツールに入力された16桁のERCシリアル番号はブラウザ上で処理され、当社のサーバーには保存・記録・送信されません。すべての計算はお客様のデバイス上で行われます。" },
        { heading: "支払い情報", text: "クレジットを購入される場合、決済はStripeによって処理されます。当社はカード情報を保存しません。" },
        { heading: "利用データ", text: "Google Analyticsを使用して、サイト利用状況に関する匿名化された情報を収集しています。" }
      ]},
      { heading: "2. 情報の利用目的", text: "収集した情報は以下の目的で使用します：", list: ["ERCロック解除ツールの提供・維持", "クレジット購入の決済処理", "お問い合わせへの対応", "サービス改善のための利用状況分析", "Google AdSenseによる広告表示"] },
      { heading: "3. 第三者サービス", text: "本サービスは以下の第三者サービスと連携しています：", list: ["Stripe — 決済処理", "Google Analytics — 利用状況分析", "Google AdSense — 広告"] },
      { heading: "4. データ保持", text: "お問い合わせフォームの送信内容は最大12ヶ月間保持されます。ブラウザに保存されたクレジット情報は、ブラウザデータを消去するまでお客様のデバイスに残ります。" },
      { heading: "5. お客様の権利", text: "お住まいの地域によっては、個人情報へのアクセス、修正、削除を要求する権利がある場合があります。" },
      { heading: "6. 子どものプライバシー", text: "本サービスは13歳未満の子どもを対象としていません。" },
      { heading: "7. ポリシーの変更", text: "プライバシーポリシーは随時更新される場合があります。重要な変更はページ上部の「最終更新」日を更新してお知らせします。" }
    ],
    contactHeading: "8. お問い合わせ",
    contactText: "プライバシーポリシーに関するご質問は、ウェブサイトの",
    contactLink: "お問い合わせフォーム",
    contactSuffix: "からご連絡ください。"
  },
  mn: {
    title: "Нууцлалын бодлого",
    lastUpdated: "Сүүлд шинэчлэгдсэн: 2026 оны 2-р сар",
    intro: "Энэхүү Нууцлалын бодлого нь ERC Unlock нь ercunlock.com-ийг ашиглах үед мэдээллийг хэрхэн цуглуулж, ашиглаж, хамгаалдагийг тайлбарладаг.",
    sections: [
      { heading: "1. Цуглуулах мэдээлэл", text: "Холбоо барих маягтыг ашиглах үед нэр, имэйл хаяг, мессежийн агуулгыг цуглуулдаг. Оруулсан ERC кодуудыг таны браузерт боловсруулж, манай серверт хадгалахгүй. Төлбөр нь Stripe-ээр дамжуулан боловсруулагдана. Бид Google Analytics ашигладаг." },
      { heading: "2. Мэдээлэл ашиглалт", text: "Цуглуулсан мэдээллийг үйлчилгээ үзүүлэх, төлбөр боловсруулах, хариу өгөх, үйлчилгээ сайжруулах зорилгоор ашигладаг." },
      { heading: "3. Гуравдагч этгээдийн үйлчилгээ", text: "Бид Stripe, Google Analytics, Google AdSense-тэй хамтран ажилладаг." },
      { heading: "4. Өгөгдөл хадгалалт", text: "Холбоо барих маягтын илгээлтүүдийг 12 хүртэл сарын хугацаанд хадгалдаг." },
      { heading: "5. Таны эрхүүд", text: "Байршлаасаа хамааран хувийн мэдээлэлд хандах, засах, устгах эрхтэй байж болно." },
      { heading: "6. Хүүхдийн нууцлал", text: "Энэ үйлчилгээ 13 насанд хүрээгүй хүүхдэд зориулагдаагүй." },
      { heading: "7. Бодлогын өөрчлөлт", text: "Нууцлалын бодлогыг цаг үе шинэчлэх боломжтой. Өөрчлөлтийг хуудасны дээд хэсгийн огноогоор мэдэгдэнэ." }
    ],
    contactHeading: "8. Холбоо барих",
    contactText: "Нууцлалын бодлоготой холбоотой асуулт байвал вэбсайтын",
    contactLink: "холбоо барих маягт",
    contactSuffix: "-г ашиглана уу."
  },
  ur: {
    title: "رازداری کی پالیسی",
    lastUpdated: "آخری تازہ کاری: فروری 2026",
    intro: "یہ رازداری کی پالیسی بتاتی ہے کہ ERC Unlock ercunlock.com کے استعمال کے دوران معلومات کو کیسے جمع، استعمال اور محفوظ کرتا ہے۔",
    sections: [
      { heading: "1. جمع کی جانے والی معلومات", subSections: [
        { heading: "آپ کی فراہم کردہ معلومات", text: "رابطہ فارم استعمال کرنے پر آپ کا نام، ای میل اور پیغام جمع کیا جاتا ہے۔ یہ صرف جواب دینے کے لیے استعمال ہوتا ہے۔" },
        { heading: "ERC کوڈز", text: "آپ کے داخل کردہ 16 ہندسوں کے ERC کوڈز براؤزر میں پروسیس ہوتے ہیں اور ہمارے سرورز پر محفوظ نہیں ہوتے۔" },
        { heading: "ادائیگی کی معلومات", text: "ادائیگی Stripe کے ذریعے پروسیس ہوتی ہے۔ ہم کارڈ کی تفصیلات محفوظ نہیں کرتے۔" },
        { heading: "استعمال کا ڈیٹا", text: "ہم Google Analytics استعمال کرتے ہیں تاکہ سائٹ کی بہتری کے لیے گمنام معلومات جمع کی جا سکے۔" }
      ]},
      { heading: "2. معلومات کا استعمال", text: "جمع کردہ معلومات درج ذیل مقاصد کے لیے استعمال ہوتی ہیں:", list: ["ERC ان لاک ٹول فراہم کرنا", "کریڈٹ خریداری پروسیس کرنا", "رابطہ فارم سوالات کا جواب دینا", "سروس بہتر بنانا"] },
      { heading: "3. فریق ثالث کی خدمات", text: "ہم Stripe، Google Analytics اور Google AdSense استعمال کرتے ہیں۔" },
      { heading: "4. ڈیٹا برقرار رکھنا", text: "رابطہ فارم کی معلومات 12 مہینے تک محفوظ رہتی ہیں۔ براؤزر میں محفوظ کریڈٹ ڈیٹا اس وقت تک رہتا ہے جب تک آپ براؤزر کا ڈیٹا صاف نہ کریں۔" },
      { heading: "5. آپ کے حقوق", text: "آپ کے مقام کے مطابق آپ کو ذاتی معلومات تک رسائی، اصلاح یا حذف کرنے کا حق حاصل ہو سکتا ہے۔" },
      { heading: "6. بچوں کی رازداری", text: "یہ سروس 13 سال سے کم عمر بچوں کے لیے نہیں ہے۔" },
      { heading: "7. پالیسی میں تبدیلیاں", text: "ہم وقتاً فوقتاً اس پالیسی کو اپ ڈیٹ کر سکتے ہیں۔ اہم تبدیلیاں صفحے کے اوپر تاریخ اپ ڈیٹ کر کے بتائی جائیں گی۔" }
    ],
    contactHeading: "8. رابطہ",
    contactText: "اس پالیسی کے بارے میں سوالات کے لیے ہمارا",
    contactLink: "رابطہ فارم",
    contactSuffix: "استعمال کریں۔"
  },
  ru: {
    title: "Политика конфиденциальности",
    lastUpdated: "Последнее обновление: февраль 2026",
    intro: "Настоящая Политика конфиденциальности объясняет, как ERC Unlock собирает, использует и защищает информацию при использовании сайта ercunlock.com.",
    sections: [
      { heading: "1. Собираемая информация", subSections: [
        { heading: "Информация, которую вы предоставляете", text: "При использовании контактной формы мы собираем ваше имя, адрес электронной почты и содержание сообщения. Эта информация используется исключительно для ответа на ваш запрос." },
        { heading: "Введённые ERC-коды", text: "16-значные ERC-серийные номера обрабатываются в вашем браузере и не передаются на наши серверы." },
        { heading: "Платёжная информация", text: "Платежи обрабатываются через Stripe. Мы не храним данные вашей карты." },
        { heading: "Данные об использовании", text: "Мы используем Google Analytics для сбора анонимизированной информации о посещаемости сайта." }
      ]},
      { heading: "2. Использование информации", text: "Собранная информация используется для:", list: ["Предоставления и обслуживания инструмента ERC", "Обработки платежей", "Ответов на запросы через контактную форму", "Анализа использования для улучшения сервиса"] },
      { heading: "3. Сторонние сервисы", text: "Мы сотрудничаем со следующими сторонними сервисами:", list: ["Stripe — обработка платежей", "Google Analytics — аналитика использования", "Google AdSense — реклама"] },
      { heading: "4. Хранение данных", text: "Обращения через контактную форму хранятся до 12 месяцев. Данные о кредитах хранятся в браузере до их очистки." },
      { heading: "5. Ваши права", text: "В зависимости от вашего местоположения вы можете иметь право на доступ, исправление или удаление персональных данных." },
      { heading: "6. Конфиденциальность детей", text: "Наш сервис не предназначен для детей до 13 лет." },
      { heading: "7. Изменения политики", text: "Мы можем периодически обновлять настоящую Политику. Об изменениях мы сообщаем, обновляя дату в верхней части страницы." }
    ],
    contactHeading: "8. Контакт",
    contactText: "По вопросам о настоящей Политике конфиденциальности используйте",
    contactLink: "контактную форму",
    contactSuffix: "на нашем сайте."
  }
};

function PrivacyPolicy({ language = 'en' }) {
  const t = translations[language] || translations.en;
  const prefix = language === 'en' ? '' : `/${language}`;

  useMeta({
    title: `${t.title} | ERC Unlock`,
    description: language === 'en'
      ? 'ERC Unlock privacy policy. Learn how we handle your data, cookies, payments via Stripe, and Google Analytics usage on ercunlock.com.'
      : undefined,
    canonical: `https://ercunlock.com${prefix}/privacy-policy`
  });

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ur' ? 'rtl' : 'ltr';
    document.title = `${t.title} | ERC Unlock`;
  }, [language, t.title]);

  const renderSection = (section, i) => (
    <div key={i}>
      <h2>{section.heading}</h2>
      {section.text && <p>{section.text}</p>}
      {section.list && <ul>{section.list.map((item, j) => <li key={j}>{item}</li>)}</ul>}
      {section.footer && <p>{section.footer}</p>}
      {section.subSections && section.subSections.map((sub, j) => (
        <div key={j}>
          <h3>{sub.heading}</h3>
          <p>{sub.text}</p>
          {sub.list && <ul>{sub.list.map((item, k) => <li key={k}>{item}</li>)}</ul>}
        </div>
      ))}
    </div>
  );

  return (
    <Layout language={language}>
      <div className="content-card">
        <h1>{t.title}</h1>
        <p className="subtitle">{t.lastUpdated}</p>

        <div className="prose" style={{ marginTop: 24 }}>
          <p>{t.intro}</p>
          <hr />
          {t.sections.map((section, i) => (
            <div key={i}>
              {renderSection(section, i)}
              <hr />
            </div>
          ))}
          <h2>{t.contactHeading}</h2>
          <p>
            {t.contactText}{' '}
            <Link to={`${prefix}/contact`} style={{ color: '#2563eb', textDecoration: 'underline' }}>
              {t.contactLink}
            </Link>
            {' '}{t.contactSuffix}
          </p>
        </div>
      </div>
    </Layout>
  );
}

export default PrivacyPolicy;
